/* istanbul ignore file */
import React from 'react';
import {shouldPolyfill as shouldPolyfillCanonicalLocales} from '@formatjs/intl-getcanonicallocales/should-polyfill';
import {shouldPolyfill as shouldPolyfillLocale} from '@formatjs/intl-locale/should-polyfill';
import {shouldPolyfill as shouldPolyfillPluralRules} from '@formatjs/intl-pluralrules/should-polyfill';
import {shouldPolyfill as shouldPolyfillRelativeTimeFormat} from '@formatjs/intl-relativetimeformat/should-polyfill';
import Loadable from 'react-loadable';
import {BrowserRouter} from 'react-router-dom';

import {CookiesClient} from '@stubhub/react-cookies';
import {render, logger} from '@stubhub/react-store-provider';
import {isSupportedDomain} from '@stubhub/supported-domains';

import AppWrapper from './app';
import {getFormatterLocale} from './formatter_locale';
import {StaticContext, useStaticContext} from './static-context';

async function polyfill(locale) {
  if (shouldPolyfillCanonicalLocales()) {
    await import('@formatjs/intl-getcanonicallocales/polyfill');
  }
  if (shouldPolyfillLocale()) {
    await import('@formatjs/intl-locale/polyfill');
  }

  const unsupportedPluralLocale = shouldPolyfillPluralRules(locale);
  // This locale is supported
  if (unsupportedPluralLocale) {
    await import('@formatjs/intl-pluralrules/polyfill-force');
    await import(`@formatjs/intl-pluralrules/locale-data/${unsupportedPluralLocale}`);
  }
  const unsupportedLocale = shouldPolyfillRelativeTimeFormat(locale);

  if (unsupportedLocale) {
    await import('@formatjs/intl-relativetimeformat/polyfill-force');
    await import(`@formatjs/intl-relativetimeformat/locale-data/${unsupportedLocale}`);
  }
}

export default function renderApp(App, props) {
  // Only load application on supported domains
  if (isSupportedDomain(window.location.host) || process.env.NODE_ENV !== 'production') {
    const __CLIENT__ = typeof window !== 'undefined';
    const w = __CLIENT__ && window;
    const initState = (w && w.__INITIAL_STATE__) || {};
    const {gsConfig = {}, device = {}, addFallbackRoutes} = initState;
    gsConfig.currentLocale = gsConfig.currentLocale || initState.lang;
    const appProps = {...gsConfig, device, logger, addFallbackRoutes, ...props};

    const cookiesClient = new CookiesClient();

    const browserLocale =
      (navigator.languages && navigator.languages[0]) || navigator.language || navigator.userLanguage;
    const locale = initState.lang || browserLocale;

    if (!__CLIENT__ || !w || !w.__LOCALE_DATA__) {
      const [lang] = locale.toLowerCase().split(/[_-]+/);
      polyfill(lang);
    }

    let messages;

    if (__CLIENT__ && w && w.__MESSAGES__) {
      messages = w.__MESSAGES__;
    }
    let html = null;
    if (__CLIENT__ && w && w.html) {
      const {html: windowHtml} = w;
      html = windowHtml;
    }

    const app = React.createElement(App, appProps);
    const routerApp = React.createElement(BrowserRouter, {}, app);
    const el = React.createElement(
      AppWrapper,
      {
        locale: getFormatterLocale(locale),
        messages,
        cookies: cookiesClient,
        html,
      },
      routerApp
    );

    Loadable.preloadReady()
      .then(() => {
        return render(el, initState, {cookies: cookiesClient, logger}, w.document.getElementById('root'));
      })
      .catch(logger.error);
  }
}

export {StaticContext, useStaticContext};
